import { useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { RiFileTextLine } from "@remixicon/react";
import { useAuthStore } from "@/store/authStore";
import { NavigationSidebar } from "@/features/navigation";
import { Shortlist } from "@/features/bids";
import FlightTracker from "../components/FlightTracker";
import FlightInfoCard from "../components/FlightInfoCard";
import { AircraftSearchBar } from "@/features/search";
import { useFlightStatusAutomation } from "../hooks/useFlightStatusAutomation";
import { Card, CardContent } from "@components/ui/Card";
import { Skeleton } from "@components/ui/Skeleton";

const FlightInfoCardSkeleton = () => (
  <Card className="overflow-hidden !rounded-3xl w-full md:w-[420px]">
    <CardContent className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32 !rounded-full" />
        <Skeleton className="h-6 w-20 !rounded-full" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-20 !rounded-full" />
          <Skeleton className="h-5 w-5 !rounded-full" />
          <Skeleton className="h-7 w-20 !rounded-full" />
        </div>
        <Skeleton className="h-5 w-16 !rounded-full" />
      </div>
      <div className="flex items-center gap-4">
        <Skeleton className="h-4 w-24 !rounded-full" />
        <Skeleton className="h-4 w-20 !rounded-full" />
      </div>
    </CardContent>
  </Card>
);

const TrackFlightView = () => {
  const { flightId, tailNumber } = useParams();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();
  const [liveData, setLiveData] = useState(null);

  const { data: flightData, isLoading, isFetching } = useQuery({
    queryKey: ['flightDetails', flightId],
    queryFn: () => apiClient.getFlights({
      flight_id: flightId,
      include: 'quotes,quotes.client,aircrafts,aircrafts.operator',
    }),
    enabled: !!flightId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // call flight by by flightid

  const flight = Array.isArray(flightData) ? flightData[0] : flightData;
  const activeTailNumber = tailNumber || flight?.aircraft?.tail_number || null;

  // Flight summary for when live data is not available
  const flightDatetimeFrom = useMemo(() => {
    if (!flight?.departure_date) return null;
    const d = new Date(flight.departure_date);
    d.setDate(d.getDate() - 1);
    return d.toISOString();
  }, [flight?.departure_date]);

  const flightDatetimeTo = useMemo(() => {
    if (!flight?.departure_date) return null;
    const d = new Date(flight.departure_date);
    d.setDate(d.getDate() + 2);
    return d.toISOString();
  }, [flight?.departure_date]);

  // const { data: flightSummary } = useQuery({
  //   queryKey: ['flightSummary', activeTailNumber, flightDatetimeFrom, flightDatetimeTo],
  //   queryFn: () => apiClient.getFlightSummary(activeTailNumber, flightDatetimeFrom, flightDatetimeTo),
  //   enabled: !!activeTailNumber && !!flightDatetimeFrom,
  //   staleTime: 5 * 60 * 100000,
  // });

  const handleFlightData = useCallback((data) => {
    setLiveData(data);
  }, []);

  // Automated status transitions: DELAYED, IN_TRANSIT, COMPLETED, DIVERTED
  useFlightStatusAutomation(flight, liveData);

  return createPortal(
    <>
      {/* Sidebar + Shortlist for authenticated users */}
      {isAuthenticated && (
        <div className="group/nav relative z-[35]">
          <NavigationSidebar onNavigate={(route) => navigate(route)} />
          <div className="fixed bottom-6 left-6 z-40 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-2 w-16 group-hover/nav:w-56 hover:w-56 transition-all duration-300 ease-in-out group-hover/nav:px-4 hover:px-4 hidden md:flex flex-col gap-2 group/shortlist">
            <Shortlist
              onViewBid={(bid) => console.log('View bid:', bid)}
              useRelativePosition={true}
            />
            <button
              onClick={() => navigate('/quotes/generate?quickquote=true')}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white p-3 rounded-xl hover:bg-primary-700 transition-colors w-full"
            >
              <RiFileTextLine className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm hidden group-hover/nav:inline group-hover/shortlist:inline whitespace-nowrap">Quick Quote</span>
            </button>
          </div>
        </div>
      )}

      {/* Map fills entire viewport - below sidebar z-[30] */}
      <div className="fixed inset-0 z-[25]">
        <FlightTracker dbFlight={flight} tailNumber={activeTailNumber} onFlightData={handleFlightData} />
      </div>

      {/* Header bar with title and search */}
      <div className={`fixed top-0 left-0 right-0 z-[29] ${isAuthenticated ? 'ml-0 md:ml-24' : 'ml-0'} px-4 md:px-6 py-3 md:py-4`}>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl md:text-5xl font-semibold text-primary-600 shrink-0">
            Track Flight
          </h1>
          {isAuthenticated && (
            <div className="hidden md:block w-96">
              <AircraftSearchBar placeholder="Search Aircraft" />
            </div>
          )}
        </div>
      </div>

      {/* Info card overlay - positioned below header, right of sidebar */}
      <div className={`fixed top-12 md:top-20 left-0 right-0 md:right-auto ${isAuthenticated ? 'md:left-28' : 'md:left-6'} z-[29] px-4 md:px-0`}>
        {flight ? (
          <FlightInfoCard flight={flight} liveData={liveData} tailSet={tailNumber} />
        ) : (isLoading || isFetching) ? (
          <FlightInfoCardSkeleton />
        ) : null}
      </div>
    </>,
    document.body
  );
};

export default TrackFlightView;
